import React, { useState, useEffect } from 'react';
import { showToast } from '../../utils/toastUtils';
import { formatDateTimeIST } from '../../utils/dateUtils';
import { apiCall } from '../../utils/apiUtils';
import { useAuth } from '../Auth/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Blob download helper. Consume (PDF) and bulk (zip) responses are binary streams;
// apiCall force-parses JSON and cannot handle them (A2), so we use raw fetch + blob here.
const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const parseFilename = (contentDisposition, fallback) => {
  if (!contentDisposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : fallback;
};

const DocumentSplitView = ({ order }) => {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, available, consumed
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  // confirmState = { mode: 'single' | 'bulk', docIds: [...] } — set only by explicit
  // user action; the confirm dialog is what triggers the network call (D-01: never open=consume).
  const [confirmState, setConfirmState] = useState(null);
  const [consuming, setConsuming] = useState(false);
  // bulkSummary = array of { document_id, result, reason } from the X-Consume-Manifest header (D-06).
  const [bulkSummary, setBulkSummary] = useState(null);
  const [rollingBackId, setRollingBackId] = useState(null);
  const itemsPerPage = 25;

  useEffect(() => {
    fetchDocuments();
  }, [order]);

  const fetchDocuments = async () => {
    try {
      const data = await apiCall(`/api/internal/orders/${order.id}/single-documents`);
      setDocuments(data.documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
      showToast.error('Failed to load document details');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (filter === 'all') return true;
    return doc.status === filter;
  });

  // Pagination
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const availableDocs = filteredDocuments.filter(doc => doc.status === 'available');
  const allAvailableSelected =
    availableDocs.length > 0 && availableDocs.every(doc => selectedIds.includes(doc.id));

  const toggleSelect = (docId) => {
    setSelectedIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleSelectAll = () => {
    if (allAvailableSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(availableDocs.map(doc => doc.id));
    }
  };

  // --- Consume actions (network calls happen ONLY after confirm) ---

  const consumeSingle = async (docId) => {
    const response = await fetch(
      `${BACKEND_URL}/api/internal/orders/${order.id}/single-documents/${docId}/consume`,
      { method: 'POST', credentials: 'include' }
    );
    if (!response.ok) {
      let detail;
      try { detail = (await response.json()).detail; } catch (e) { detail = null; }
      throw new Error(detail || 'Failed to consume page');
    }
    const blob = await response.blob();
    const filename = parseFilename(
      response.headers.get('Content-Disposition'),
      `page-${docId}.pdf`
    );
    triggerBlobDownload(blob, filename);
  };

  const consumeBulk = async (docIds) => {
    const response = await fetch(
      `${BACKEND_URL}/api/internal/orders/${order.id}/single-documents/consume-bulk`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_ids: docIds }),
      }
    );
    if (!response.ok) {
      let detail;
      try { detail = (await response.json()).detail; } catch (e) { detail = null; }
      throw new Error(detail || 'Failed to consume pages');
    }
    // Per-page result manifest is surfaced in the X-Consume-Manifest response header
    // (JSON array of { document_id, result, reason }; result ∈ consumed|reincluded|skipped|failed).
    let manifest = [];
    const manifestHeader = response.headers.get('X-Consume-Manifest');
    if (manifestHeader) {
      try { manifest = JSON.parse(manifestHeader); } catch (e) { manifest = []; }
    }
    const data = await response.json();
    // Bulk endpoint returns JSON { results, manifest, zip_base64, filename } — the zip
    // bytes are base64-encoded in the body (not a raw application/zip stream). Decode
    // to a Blob before download. Fall back to the body manifest if the header is absent.
    if (data.zip_base64) {
      const byteChars = atob(data.zip_base64);
      const byteArray = Uint8Array.from(byteChars, (c) => c.charCodeAt(0));
      const blob = new Blob([byteArray], { type: 'application/zip' });
      triggerBlobDownload(blob, data.filename || 'consumed_documents.zip');
    }
    if (manifest.length === 0 && Array.isArray(data.manifest)) {
      manifest = data.manifest;
    }
    return { data, manifest };
  };

  // Admin-only rollback (D-09/D-10). Uses apiCall (JSON), NOT the blob fetch. The
  // control itself is rendered only for admins; the server enforces admin-only 403.
  const handleRollback = async (docId) => {
    setRollingBackId(docId);
    try {
      await apiCall(
        `/api/internal/orders/${order.id}/single-documents/${docId}/rollback`,
        { method: 'POST', data: { reason: 'Admin reverted via API Documents view' } }
      );
      showToast.success('Page returned to available');
      await fetchDocuments();
    } catch (error) {
      console.error('Rollback failed:', error);
      showToast.error(error.message || 'Failed to return page to available');
    } finally {
      setRollingBackId(null);
    }
  };

  const openConfirm = (mode, docIds) => {
    if (!docIds || docIds.length === 0) return;
    setConfirmState({ mode, docIds });
  };

  const handleConfirm = async () => {
    if (!confirmState) return;
    const { mode, docIds } = confirmState;
    setConsuming(true);
    try {
      if (mode === 'single') {
        await consumeSingle(docIds[0]);
        showToast.success('Page consumed and downloaded');
      } else {
        const { manifest } = await consumeBulk(docIds);
        setBulkSummary(manifest);
        const consumed = manifest.filter(m => m.result === 'consumed').length;
        showToast.success(
          `Bulk consume complete: ${consumed} of ${docIds.length} page(s) drawn down`
        );
      }
      setSelectedIds([]);
      setConfirmState(null);
      await fetchDocuments();
    } catch (error) {
      console.error('Consume failed:', error);
      showToast.error(error.message || 'Consume failed');
    } finally {
      setConsuming(false);
    }
  };

  // Truncate long IDs (request ids, page ids) so the extra Actions column no longer
  // forces horizontal scroll; full value stays available via the cell's title tooltip.
  const truncateId = (value, head = 10) =>
    value && value.length > head ? `${value.slice(0, head)}…` : value;

  const getStatusBadge = (status) => {
    if (status === 'consumed') {
      return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Consumed</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">Available</span>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No split documents available for this order.</p>
        <p className="text-sm mt-2">Upload a full order document to see split pages here.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h4 className="text-lg font-semibold">API Document Delivery</h4>
          <p className="text-sm text-gray-600">
            Total Pages: {documents.length}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1 rounded-md text-sm ${filter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('available')}
            className={`px-3 py-1 rounded-md text-sm ${filter === 'available' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Available
          </button>
          <button
            onClick={() => setFilter('consumed')}
            className={`px-3 py-1 rounded-md text-sm ${filter === 'consumed' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
          >
            Consumed
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3">
          <span className="text-sm text-indigo-800">
            {selectedIds.length} page(s) selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
            <button
              onClick={() => openConfirm('bulk', selectedIds)}
              className="px-4 py-1.5 rounded-md text-sm bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Consume selected
            </button>
          </div>
        </div>
      )}

      {/* Per-page bulk result summary (D-06) — never hides skipped/failed pages */}
      {bulkSummary && bulkSummary.length > 0 && (
        <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-semibold text-gray-800">Bulk consume result</h5>
            <button
              onClick={() => setBulkSummary(null)}
              className="text-xs text-gray-500 hover:text-gray-800"
            >
              Dismiss
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs">
            <span className="text-green-700">
              Consumed: {bulkSummary.filter(m => m.result === 'consumed').length}
            </span>
            <span className="text-blue-700">
              Re-included: {bulkSummary.filter(m => m.result === 'reincluded').length}
            </span>
            <span className="text-yellow-700">
              Skipped: {bulkSummary.filter(m => m.result === 'skipped').length}
            </span>
            <span className="text-red-700">
              Failed: {bulkSummary.filter(m => m.result === 'failed').length}
            </span>
          </div>
          {bulkSummary.some(m => m.result === 'skipped' || m.result === 'failed') && (
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              {bulkSummary
                .filter(m => m.result === 'skipped' || m.result === 'failed')
                .map((m) => (
                  <li key={m.document_id}>
                    <span className="font-mono">{m.document_id}</span>
                    {' — '}
                    <span className={m.result === 'failed' ? 'text-red-700' : 'text-yellow-700'}>
                      {m.result}
                    </span>
                    {m.reason ? `: ${m.reason}` : ''}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allAvailableSelected}
                  onChange={toggleSelectAll}
                  disabled={availableDocs.length === 0}
                  aria-label="Select all available pages"
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consumed By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consumed At</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedDocuments.map((doc) => (
              <tr key={doc.id} className="hover:bg-gray-50">
                <td className="px-4 py-4 whitespace-nowrap">
                  {doc.status === 'available' && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(doc.id)}
                      onChange={() => toggleSelect(doc.id)}
                      aria-label={`Select page ${doc.page_number}`}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                    />
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  Page {doc.page_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(doc.status)}
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono"
                  title={doc.consumed_by_request_id || ''}
                >
                  {doc.consumed_by_request_id ? truncateId(doc.consumed_by_request_id) : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {doc.consumed_at ? formatDateTimeIST(doc.consumed_at) : '-'}
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-xs text-gray-400 font-mono"
                  title={doc.id}
                >
                  {truncateId(doc.id)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {doc.status === 'available' && (
                    <button
                      onClick={() => openConfirm('single', [doc.id])}
                      className="px-3 py-1 rounded-md text-xs bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      Consume
                    </button>
                  )}
                  {/* Admin-only rollback on consumed rows (D-10); clients never see it. */}
                  {doc.status === 'consumed' && isAdmin && (
                    <button
                      onClick={() => handleRollback(doc.id)}
                      disabled={rollingBackId === doc.id}
                      className="px-3 py-1 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      {rollingBackId === doc.id ? 'Returning…' : 'Return to available'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {filteredDocuments.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
            <div className="text-sm text-gray-700">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredDocuments.length)} - {Math.min(currentPage * itemsPerPage, filteredDocuments.length)} of {filteredDocuments.length} documents
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Finality confirm dialog (D-01/D-02) — honest about permanence, shown BEFORE any consume call */}
      {confirmState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900">Confirm consumption</h3>
            <p className="mt-3 text-sm text-gray-700">
              You are about to consume{' '}
              <strong>
                {confirmState.docIds.length} page{confirmState.docIds.length > 1 ? 's' : ''}
              </strong>.
            </p>
            <p className="mt-2 text-sm text-gray-700">
              This permanently draws down the selected page(s) from your stamp-paper balance.
              This <strong>cannot be undone by you</strong> — once consumed, please contact
              support or an administrator if a reversal is genuinely needed. Consumed pages
              remain re-downloadable.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setConfirmState(null)}
                disabled={consuming}
                className="px-4 py-2 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={consuming}
                className="px-4 py-2 rounded-md text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {consuming ? 'Consuming…' : 'Consume permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSplitView;
